import React, { useEffect, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { workImages } from "../../../data/data";
import { useTranslation } from "react-i18next";
import Modal from "../../common/CustomModal";

const WorkCompleted = () => {
  const { t } = useTranslation();
  const [currentImageIndexes, setCurrentImageIndexes] = useState<{
    [key: number]: number;
  }>(workImages.reduce((acc, _, idx) => ({ ...acc, [idx]: 0 }), {}));

  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    groupIndex: number;
    imageIndex: number;
  } | null>(null);

  const [imageLoading, setImageLoading] = useState<{ [key: number]: boolean }>(
    {}
  );
  useEffect(() => {
    workImages.forEach((work, idx) => {
      const nextIndex = Math.min(
        currentImageIndexes[idx] + 1,
        work.images.length - 1
      );
      const prevIndex = Math.max(currentImageIndexes[idx] - 1, 0);

      const nextImg = new Image();
      nextImg.src = work.images[nextIndex];

      const prevImg = new Image();
      prevImg.src = work.images[prevIndex];
    });
  }, [currentImageIndexes]);

  const handleImageChange = (index: number, direction: "next" | "prev") => {
    setCurrentImageIndexes((prev) => ({
      ...prev,
      [index]:
        direction === "next"
          ? Math.min(prev[index] + 1, workImages[index].images.length - 1)
          : Math.max(prev[index] - 1, 0),
    }));
  };

  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && selectedImage.imageIndex > 0) {
        const newIndex = selectedImage.imageIndex - 1;
        setSelectedImage({
          url: workImages[selectedImage.groupIndex].images[newIndex],
          groupIndex: selectedImage.groupIndex,
          imageIndex: newIndex,
        });
      }

      if (e.key === "ArrowRight") {
        const group = workImages[selectedImage.groupIndex];
        if (selectedImage.imageIndex < group.images.length - 1) {
          const newIndex = selectedImage.imageIndex + 1;
          setSelectedImage({
            url: group.images[newIndex],
            groupIndex: selectedImage.groupIndex,
            imageIndex: newIndex,
          });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage]);
  return (
    <section className="w-full">
      <div className=" maxContainer">
        <div className="flex flex-col items-center w-full">
          <div className="grid w-full grid-cols-2 mt-12 md:grid-cols-3 ">
            {workImages.map((work, idx) => (
              <div
                key={idx}
                className="relative cursor-pointer ss:h-[274px] border w-full overflow-hidden group"
                onClick={() =>
                  setSelectedImage({
                    url: work.images[currentImageIndexes[idx]],
                    groupIndex: idx,
                    imageIndex: currentImageIndexes[idx],
                  })
                }
              >
                <div
                  className="absolute inset-0 transition-transform duration-300 ease-in-out transform bg-center bg-cover group-hover:scale-105"
                  style={{
                    backgroundImage: `url(${work.images[currentImageIndexes[idx]]})`,
                  }}
                />
                {imageLoading[idx] && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
                )}

                <img
                  src={work.images[currentImageIndexes[idx]]}
                  alt="gallery-img"
                  className={`object-cover w-full h-full transition-opacity duration-500 ease-in-out ${
                    imageLoading[idx] ? "opacity-0" : "opacity-100"
                  }`}
                  loading="lazy"
                  onLoad={() =>
                    setImageLoading((prev) => ({ ...prev, [idx]: false }))
                  }
                />

                <div className="absolute inset-0 flex items-center justify-between transition-opacity opacity-0 ss:px-4 group-hover:opacity-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageChange(idx, "prev");
                    }}
                    className={`bg-white rounded-full p-1 ss:p-2 shadow-md hover:bg-gray-200 transition-all ${
                      currentImageIndexes[idx] === 0 ? "opacity-50 " : ""
                    }`}
                    disabled={currentImageIndexes[idx] === 0}
                  >
                    <ChevronLeftIcon />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageChange(idx, "next");
                    }}
                    className={`bg-white rounded-full p-1 ss:p-2 shadow-md hover:bg-gray-200 transition-all ${
                      currentImageIndexes[idx] === work.images.length - 1
                        ? "opacity-50 "
                        : ""
                    }`}
                    disabled={
                      currentImageIndexes[idx] === work.images.length - 1
                    }
                  >
                    <ChevronRightIcon />
                  </button>
                </div>

                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-4 transition-all duration-700 ease-in-out translate-y-5 shadow-lg opacity-0 bg-white/80 backdrop-blur-md group-hover:opacity-100 group-hover:translate-y-0">
                  <span className="text-[10px] ss:text-[14px] font-medium text-[#000000bd]">
                    {t(work.desc)}
                  </span>
                  <div className="text-[12px] text-greyText">
                    {currentImageIndexes[idx] + 1}/{work.images.length}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={!!selectedImage}
        onClose={() => {
          if (selectedImage) {
            setCurrentImageIndexes((prev) => ({
              ...prev,
              [selectedImage.groupIndex]: selectedImage.imageIndex,
            }));
          }
          setSelectedImage(null);
        }}
        size="max-content"
      >
        {selectedImage && (
          <div className="relative flex items-center justify-center w-full">
            <button
              onClick={() => {
                if (selectedImage.imageIndex > 0) {
                  const newIndex = selectedImage.imageIndex - 1;
                  setSelectedImage({
                    url: workImages[selectedImage.groupIndex].images[newIndex],
                    groupIndex: selectedImage.groupIndex,
                    imageIndex: newIndex,
                  });
                }
              }}
              disabled={selectedImage.imageIndex === 0}
              className={`absolute left-0 z-10 p-2 m-2 bg-white rounded-full shadow-md hover:bg-gray-200 ${
                selectedImage.imageIndex === 0
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <ChevronLeftIcon />
            </button>

            <img
              src={
                workImages[selectedImage.groupIndex].images[
                  selectedImage.imageIndex
                ]
              }
              className="object-contain max-h-[80vh] w-auto max-w-[90vw] mx-auto"
              alt="completed work"
            />

            <button
              onClick={() => {
                const group = workImages[selectedImage.groupIndex];
                if (selectedImage.imageIndex < group.images.length - 1) {
                  const newIndex = selectedImage.imageIndex + 1;
                  setSelectedImage({
                    url: group.images[newIndex],
                    groupIndex: selectedImage.groupIndex,
                    imageIndex: newIndex,
                  });
                }
              }}
              disabled={
                selectedImage.imageIndex ===
                workImages[selectedImage.groupIndex].images.length - 1
              }
              className={`absolute right-0 z-10 p-2 m-2 bg-white rounded-full shadow-md hover:bg-gray-200 ${
                selectedImage.imageIndex ===
                workImages[selectedImage.groupIndex].images.length - 1
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default WorkCompleted;
